// Pool and Entry type definitions

export enum PoolType {
  CFP = 'CFP',
  NFL_PLAYOFF = 'NFL_PLAYOFF',
  NHL_PLAYOFF = 'NHL_PLAYOFF',
  NBA_PLAYOFF = 'NBA_PLAYOFF',
  FOUR_NATIONS = 'FOUR_NATIONS',
  OLYMPIC_HOCKEY = 'OLYMPIC_HOCKEY',
  NCAA_BASKETBALL_PICK6 = 'NCAA_BASKETBALL_PICK6',
  NCAA_BASKETBALL_KNOCKOUT = 'NCAA_BASKETBALL_KNOCKOUT',
  FORMULA_1 = 'FORMULA_1',
  WORLD_CUP_PICK6 = 'WORLD_CUP_PICK6',
  WORLD_CUP_SURVIVOR = 'WORLD_CUP_SURVIVOR',
  NFL_NO_REPEAT = 'NFL_NO_REPEAT',
}

export enum PoolStatus {
  OPEN = 'OPEN',
  LOCKED = 'LOCKED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export interface Pool {
  id: string;
  slug?: string; // URL-friendly identifier (e.g., "2026-f1-pool")
  name: string;
  type: PoolType;
  description?: string;
  isPublic: boolean;
  entryFee?: string;
  maxEntries?: number;
  deadline: string;
  status: PoolStatus;
  prizeStructure?: Record<string, number>;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    entries: number;
  };
  uniqueLineups?: number; // Number of unique lineups (Olympic Hockey only)
  teams?: PoolTeam[]; // Teams associated with this pool (included when fetched with relations)
}

export interface Team {
  id: string;
  name: string;
  abbreviation: string;
  shortName?: string;
  conference?: string;
  division?: string;
  seed?: number;
}

// PoolTeam represents a team's association with a specific pool
export interface PoolTeam {
  poolId: string;
  teamId: string;
  seed?: number;
  metadata?: {
    region?: string;  // For NCAA Basketball pools (East, West, South, Midwest)
    [key: string]: any;
  };
  team: Team;
}

export interface Player {
  id: string;
  name: string;
  teamId: string;
  position: string;
  team: Team;
}

export interface PlayerPick {
  playerId: string;
  playerName: string;
  teamId: string;
  position: string;
}

export interface RosterSelectionPicks {
  type: 'ROSTER_SELECTION';
  positions: Record<string, PlayerPick>;
}

// F1 Types
export type F1Phase = 'REGULAR' | 'PLAYOFF' | 'ELIMINATED';

export interface F1DriverPick {
  driverId: string;
  driverName: string;
  teamId: string;
  teamName?: string;
}

export interface F1RacePick {
  raceId: string;
  raceNumber: number;
  raceName?: string;
  drivers: F1DriverPick[];
}

export interface F1DriverUsage {
  raceIds: string[];
  count: number;
}

export interface F1EntryPicks {
  type: 'F1_SEQUENTIAL_RACES';
  season: number;
  phase: F1Phase;
  driverUsageHistory: Record<string, F1DriverUsage>;
  picks: F1RacePick[];
  regularSeasonScore?: number;
  playoffStartRace?: number;
  participationRank?: number;
}

// F1 Scoring Tables
export const F1_RACE_POINTS = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1]; // Positions 1-10
export const F1_SPRINT_POINTS = [8, 7, 6, 5, 4, 3, 2, 1]; // Positions 1-8

// F1 Constants
export const F1_REGULAR_SEASON_RACES = 20;
export const F1_TOTAL_RACES = 24;
export const F1_DRIVERS_PER_RACE = 2;
export const F1_REGULAR_MAX_USES = 2;
export const F1_PLAYOFF_MAX_USES = 1;
export const F1_PLAYOFF_ADVANCEMENT_PERCENT = 0.4;

// Type guard to check if picks are F1 type
export function isF1Picks(picks: EntryPicks | undefined | null): picks is F1EntryPicks {
  return picks?.type === 'F1_SEQUENTIAL_RACES';
}

// NFL No-Repeat Challenge Types — full regular season (Weeks 1-18), weekly 7-player offense lineup.
// Picks are keyed JSON in Entry.picks (storage approach A); Entry.score holds the season total.
export const NFL_NO_REPEAT_TOTAL_WEEKS = 18;

export interface WeeklyLineupWeek {
  // Keys: QB, RB1, RB2, WR1, WR2, TE, FLEX. A slot may be omitted (empty = scores 0).
  positions: Record<string, PlayerPick>;
  weekScore: number;
}

export interface NflNoRepeatPlayerUsage {
  weeks: number[];
  count: number;
}

export interface WeeklyLineupPicks {
  type: 'WEEKLY_LINEUP';
  weeks: Record<string, WeeklyLineupWeek>; // key = week number as string '1'..'18'
  playerUsageHistory: Record<string, NflNoRepeatPlayerUsage>; // keyed by playerId (trade-proof)
}

// Type guard to check if picks are NFL No-Repeat weekly-lineup type
export function isWeeklyLineupPicks(picks: EntryPicks | undefined | null): picks is WeeklyLineupPicks {
  return picks?.type === 'WEEKLY_LINEUP';
}

// Union type for all pick formats
export type EntryPicks =
  | RosterSelectionPicks
  | KnockoutEntryPicks
  | Pick6EntryPicks
  | F1EntryPicks
  | WorldCupPick6EntryPicks
  | WorldCupSurvivorEntryPicks
  | WeeklyLineupPicks;

// Type guard to check if picks are roster selection type
export function isRosterSelectionPicks(picks: EntryPicks | undefined | null): picks is RosterSelectionPicks {
  return picks?.type === 'ROSTER_SELECTION';
}

// Type guard to check if picks are knockout type
export function isKnockoutPicks(picks: EntryPicks | undefined | null): picks is KnockoutEntryPicks {
  return picks?.type === 'DAILY_PICKS';
}

// Type guard to check if picks are pick6 type
export function isPick6Picks(picks: EntryPicks | undefined | null): picks is Pick6EntryPicks {
  return picks?.type === 'BRACKET_PREDICTION';
}

// NCAA Basketball Knockout Types
export type KnockoutEntryStatus = 'ALIVE' | 'ELIMINATED' | 'BOUGHT_BACK';
export type KnockoutPickResult = 'PENDING' | 'WIN' | 'LOSS';

export interface KnockoutTeamPick {
  teamId: string;
  teamName: string;
  seed?: number;
  region?: string;
  result?: KnockoutPickResult;
}

export interface KnockoutEntryPicks {
  type: 'DAILY_PICKS';
  status: KnockoutEntryStatus;
  buyBackCount: number;
  eliminatedOn: string | null;
  usedTeams: string[];
  picks: Record<string, KnockoutTeamPick[]>; // Key is date (YYYY-MM-DD)
}

// NCAA Basketball Pick6 Types
export interface Pick6TeamPick {
  teamId: string;
  teamName: string;
  seed: number;
  region?: string;
}

export interface Pick6EntryPicks {
  type: 'BRACKET_PREDICTION';
  teams: Pick6TeamPick[];
}

// Pick6 Scoring: Points = Seed × Round Factor
export const PICK6_ROUND_MULTIPLIERS: Record<string, number> = {
  'Round of 64': 1,
  'Round of 32': 3,
  'Sweet 16': 5,
  'Elite 8': 7,
  'Final Four': 9,
  'Championship': 11,
};

// Pick6 Constants
export const PICK6_TEAM_COUNT = 6;

// =============================================
// World Cup Pick 6 Types
// =============================================

export interface WorldCupTeamPick {
  teamId: string;
  teamName: string;
  shortName?: string;
  seed: number;       // 1–12 (FIFA ranking tier)
  isoCode: string;    // ISO 3166-1 alpha-2 (lowercase) for flag rendering
}

export interface WorldCupPick6EntryPicks {
  type: 'WORLD_CUP_BRACKET_PREDICTION';
  teams: WorldCupTeamPick[];
}

// Type guard
export function isWorldCupPick6Picks(
  picks: EntryPicks | undefined | null,
): picks is WorldCupPick6EntryPicks {
  return picks?.type === 'WORLD_CUP_BRACKET_PREDICTION';
}

// World Cup Pick 6 Scoring: Points = Seed × Round Factor
// Group-stage advancement is awarded once when a team reaches the Round of 32.
export const WORLD_CUP_PICK6_ROUND_MULTIPLIERS: Record<string, number> = {
  'Group Stage': 4,
  'Round of 32': 5,
  'Round of 16': 7,
  'Quarterfinals': 9,
  'Semifinals': 11,
  'Final': 13,
};

export const WORLD_CUP_PICK6_TEAM_COUNT = 6;
export const WORLD_CUP_SEED_MIN = 1;
export const WORLD_CUP_SEED_MAX = 12;

// =============================================
// World Cup Survivor Types
// =============================================

export type WorldCupSurvivorStatus = 'ALIVE' | 'ELIMINATED';
export type WorldCupKnockoutResult = 'PENDING' | 'WIN' | 'LOSS';
export type WorldCupGroupAdvanced = 'PENDING' | 'YES' | 'NO';

export type WorldCupKnockoutRoundKey = 'R32' | 'R16' | 'QF' | 'SF' | 'FINAL';

export const WORLD_CUP_KNOCKOUT_ROUNDS: {
  key: WorldCupKnockoutRoundKey;
  label: string;
  matches: number;
}[] = [
  { key: 'R32',   label: 'Round of 32',   matches: 16 },
  { key: 'R16',   label: 'Round of 16',   matches: 8 },
  { key: 'QF',    label: 'Quarterfinals', matches: 4 },
  { key: 'SF',    label: 'Semifinals',    matches: 2 },
  { key: 'FINAL', label: 'Final',         matches: 1 },
];

export const WORLD_CUP_SURVIVOR_GROUP_PICK_COUNT = 6;

export interface WorldCupSurvivorGroupPick extends WorldCupTeamPick {
  advanced?: WorldCupGroupAdvanced;
}

export interface WorldCupSurvivorKnockoutPick extends WorldCupTeamPick {
  result?: WorldCupKnockoutResult;
}

export interface WorldCupSurvivorEntryPicks {
  type: 'WORLD_CUP_SURVIVOR';
  status: WorldCupSurvivorStatus;
  eliminatedRound: WorldCupKnockoutRoundKey | 'GROUP' | null;
  groupStageTeams: WorldCupSurvivorGroupPick[];
  knockoutPicks: Partial<Record<WorldCupKnockoutRoundKey, WorldCupSurvivorKnockoutPick>>;
}

// Type guard
export function isWorldCupSurvivorPicks(
  picks: EntryPicks | undefined | null,
): picks is WorldCupSurvivorEntryPicks {
  return picks?.type === 'WORLD_CUP_SURVIVOR';
}

// NCAA Tournament Team (for team selection)
export interface NCAATeam {
  id: string;
  name: string;
  seed: number;
  region: 'East' | 'West' | 'South' | 'Midwest';
  conference?: string;
  bracketDay?: number; // 1 or 2 — which day of each round the team plays (derived from R64 schedule)
}

// Knockout Round Information
export interface KnockoutRound {
  name: string;
  date: string;
  picksRequired: number;
  buyBackAvailable: boolean;
}

// Knockout pick requirements by tournament round
// Note: buy-back costs $10 (monetary fee, not a scoring penalty)
// buyBackPicksRequired = total picks needed on the day you buy back (replaces normal picksRequired)
export const KNOCKOUT_ROUND_REQUIREMENTS: Record<string, { picksRequired: number; buyBackAvailable: boolean; buyBackPicksRequired?: number }> = {
  'Round of 64 Day 1': { picksRequired: 2, buyBackAvailable: true },
  'Round of 64 Day 2': { picksRequired: 2, buyBackAvailable: true, buyBackPicksRequired: 4 },
  'Round of 32 Day 1': { picksRequired: 1, buyBackAvailable: true, buyBackPicksRequired: 5 },
  'Round of 32 Day 2': { picksRequired: 1, buyBackAvailable: false, buyBackPicksRequired: 6 },
  'Sweet 16': { picksRequired: 1, buyBackAvailable: false },
  'Elite 8': { picksRequired: 1, buyBackAvailable: false },
  'Final Four': { picksRequired: 1, buyBackAvailable: false },
  'Championship': { picksRequired: 1, buyBackAvailable: false },
};

// Payment types
export type PaymentStatus = 'NOT_REQUIRED' | 'UNPAID' | 'PAID';
export type PaymentMethod = 'PAYPAL' | 'CASHAPP' | 'VENMO' | 'CASH' | 'COMP' | 'OTHER';

export interface PaymentInfo {
  requiresPayment: boolean;
  entryFee: number;
  poolName?: string;
  paymentMethods: PaymentMethodInfo[];
}

export interface PaymentMethodInfo {
  method: PaymentMethod;
  label: string;
  handle: string;
  paymentUrl: string;
  /** true when the pay link prefills the note with the pay code (Venmo) */
  notePrefilled?: boolean;
}

export interface PaymentSettings {
  paypalLink?: string;
  cashappHandle?: string;
  venmoHandle?: string;
}

// ---- Payment Tracking v2 ----

/** One unpaid entry on the user's balance (Settle Up page). */
export interface BalanceEntry {
  entryId: string;
  poolId: string;
  poolSlug?: string | null;
  poolName: string;
  entryName?: string | null;
  amount: number;
  createdAt: string;
}

/** GET /users/me/balance */
export interface UserBalance {
  payCode: string;
  totalOwed: number;
  entryCount: number;
  entries: BalanceEntry[];
  paymentMethods: PaymentMethodInfo[];
}

/** One unpaid entry inside the admin Owed view. */
export interface OwedEntry {
  entryId: string;
  poolId: string;
  poolSlug?: string | null;
  poolName: string;
  entryName?: string | null;
  amount: number;
  poolLocked: boolean;
  createdAt: string;
}

/** One person who owes money (admin Owed view). */
export interface OwedPerson {
  userId: string;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  payCode?: string | null;
  totalOwed: number;
  hasLockedUnpaid: boolean;
  entries: OwedEntry[];
}

/** GET /admin/payments/owed */
export interface OwedSummary {
  success: boolean;
  peopleCount: number;
  entryCount: number;
  totalOutstanding: number;
  people: OwedPerson[];
}

export interface Entry {
  id: string;
  poolId: string;
  userId: string;
  entryName?: string;
  picks: EntryPicks;
  score: string;
  tiebreaker?: number;
  // Payment fields
  paymentStatus?: PaymentStatus;
  paymentMethod?: PaymentMethod;
  paymentReference?: string;
  paymentNote?: string;
  paidAt?: string;
  amountOwed?: string;
  createdAt: string;
  updatedAt: string;
  username?: string;      // Display username from backend
  displayName?: string;   // Computed display name from backend
  user?: {
    id: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  // Knockout-specific fields (only present for NCAA_BASKETBALL_KNOCKOUT pools)
  knockoutStatus?: KnockoutEntryStatus;  // ALIVE, ELIMINATED, or BOUGHT_BACK
  daysPlayed?: number;                    // Number of days with picks submitted
  winningPicks?: number;                  // Number of winning picks
  buyBackCount?: number;                  // Number of buy-backs used (0-3)
  eliminatedOn?: string | null;           // Date eliminated (YYYY-MM-DD)
  prizeWon?: number;                      // Prize money won (for completed pools)
}

export interface CreateEntryRequest {
  entryName?: string;
  picks: EntryPicks;
  tiebreaker?: number;
}

export interface CreatePoolRequest {
  name: string;
  type: PoolType;
  description?: string;
  isPublic?: boolean;
  entryFee?: number;
  maxEntries?: number;
  deadline: string;
  prizeStructure?: Record<string, number>;
}

// CFP Position requirements
export const CFP_POSITIONS = {
  QB: { count: 1, label: 'Quarterback', eligible: ['QB'] },
  RB: { count: 2, label: 'Running Back', eligible: ['RB'] },
  WR: { count: 2, label: 'Wide Receiver', eligible: ['WR'] },
  TE: { count: 1, label: 'Tight End', eligible: ['TE'] },
  FLEX: { count: 2, label: 'Flex (RB/WR/TE)', eligible: ['RB', 'WR', 'TE'] },
  K: { count: 1, label: 'Kicker', eligible: ['K'] },
  DEF: { count: 1, label: 'Defense', eligible: ['DEF'] },
} as const;

export type CFPPosition = keyof typeof CFP_POSITIONS;

// NFL Position requirements
export const NFL_POSITIONS = {
  QB: { count: 1, label: 'Quarterback', eligible: ['QB'] },
  RB: { count: 2, label: 'Running Back', eligible: ['RB'] },
  WR: { count: 2, label: 'Wide Receiver', eligible: ['WR'] },
  TE: { count: 1, label: 'Tight End', eligible: ['TE'] },
  FLEX: { count: 4, label: 'Flex (RB/WR/TE)', eligible: ['RB', 'WR', 'TE'] },
  K: { count: 1, label: 'Kicker', eligible: ['K'] },
  DEF: { count: 1, label: 'Defense', eligible: ['DEF'] },
} as const;

export type NFLPosition = keyof typeof NFL_POSITIONS;

// NHL Position requirements
export const NHL_POSITIONS = {
  C: { count: 2, label: 'Center', eligible: ['C'] },
  LW: { count: 2, label: 'Left Wing', eligible: ['LW'] },
  RW: { count: 2, label: 'Right Wing', eligible: ['RW'] },
  D: { count: 4, label: 'Defenseman', eligible: ['D'] },
  G: { count: 2, label: 'Goaltender', eligible: ['G'] },
} as const;

export type NHLPosition = keyof typeof NHL_POSITIONS;

// NBA Position requirements
// NBA API provides generic positions: G (Guard), F (Forward), C (Center)
export const NBA_POSITIONS = {
  G: { count: 4, label: 'Guard', eligible: ['G'] },
  F: { count: 4, label: 'Forward', eligible: ['F'] },
  C: { count: 2, label: 'Center', eligible: ['C'] },
  FLEX: { count: 2, label: 'Flex', eligible: ['G', 'F', 'C'] },
} as const;

export type NBAPosition = keyof typeof NBA_POSITIONS;

// Olympic Hockey Position requirements
export const OLYMPIC_HOCKEY_POSITIONS = {
  F1: { count: 1, label: 'Forward #1', eligible: ['F'] },
  F2: { count: 1, label: 'Forward #2', eligible: ['F'] },
  F3: { count: 1, label: 'Forward #3', eligible: ['F'] },
  F4: { count: 1, label: 'Forward #4', eligible: ['F'] },
  F5: { count: 1, label: 'Forward #5', eligible: ['F'] },
  D1: { count: 1, label: 'Defenseman #1', eligible: ['D'] },
  D2: { count: 1, label: 'Defenseman #2', eligible: ['D'] },
  D3: { count: 1, label: 'Defenseman #3', eligible: ['D'] },
  D4: { count: 1, label: 'Defenseman #4', eligible: ['D'] },
  G: { count: 1, label: 'Goaltender', eligible: ['G'] },
} as const;

export type OlympicHockeyPosition = keyof typeof OLYMPIC_HOCKEY_POSITIONS;

// NFL No-Repeat Position requirements (7 individually-named slots, one player each)
export const NFL_NO_REPEAT_POSITIONS = {
  QB: { count: 1, label: 'Quarterback', eligible: ['QB'] },
  RB1: { count: 1, label: 'Running Back', eligible: ['RB'] },
  RB2: { count: 1, label: 'Running Back', eligible: ['RB'] },
  WR1: { count: 1, label: 'Wide Receiver', eligible: ['WR'] },
  WR2: { count: 1, label: 'Wide Receiver', eligible: ['WR'] },
  TE: { count: 1, label: 'Tight End', eligible: ['TE'] },
  FLEX: { count: 1, label: 'Flex (RB/WR/TE)', eligible: ['RB', 'WR', 'TE'] },
} as const;

export type NflNoRepeatPosition = keyof typeof NFL_NO_REPEAT_POSITIONS;

// Union type for all positions
export type Position = CFPPosition | NFLPosition | NHLPosition | NBAPosition | OlympicHockeyPosition | NflNoRepeatPosition;

// Helper to get position config for a pool type
// Uses string comparison to handle API responses that may not match the enum reference
export const getPositionConfig = (poolType: PoolType | string) => {
  const type = String(poolType);
  switch (type) {
    case 'CFP':
      return CFP_POSITIONS;
    case 'NFL_PLAYOFF':
      return NFL_POSITIONS;
    case 'NHL_PLAYOFF':
      return NHL_POSITIONS;
    case 'NBA_PLAYOFF':
      return NBA_POSITIONS;
    case 'OLYMPIC_HOCKEY':
      return OLYMPIC_HOCKEY_POSITIONS;
    case 'FOUR_NATIONS':
      return OLYMPIC_HOCKEY_POSITIONS; // Same format
    case 'NFL_NO_REPEAT':
      return NFL_NO_REPEAT_POSITIONS;
    default:
      // Default to CFP for now
      return CFP_POSITIONS;
  }
};

// Country flag mapping for Olympic Hockey
const COUNTRY_FLAGS: Record<string, string> = {
  'Canada': '🇨🇦',
  'United States': '🇺🇸',
  'USA': '🇺🇸',
  'Sweden': '🇸🇪',
  'Finland': '🇫🇮',
  'Russia': '🇷🇺',
  'Czech Republic': '🇨🇿',
  'Czechia': '🇨🇿',
  'Switzerland': '🇨🇭',
  'Germany': '🇩🇪',
  'Slovakia': '🇸🇰',
  'Norway': '🇳🇴',
  'Denmark': '🇩🇰',
  'Latvia': '🇱🇻',
  'Austria': '🇦🇹',
  'France': '🇫🇷',
  'Slovenia': '🇸🇮',
  'Kazakhstan': '🇰🇿',
  'Belarus': '🇧🇾',
  'Italy': '🇮🇹',
  'Great Britain': '🇬🇧',
  'Japan': '🇯🇵',
  'South Korea': '🇰🇷',
  'China': '🇨🇳',
};

// Helper to get country flag emoji
export const getCountryFlag = (countryName: string): string => {
  return COUNTRY_FLAGS[countryName] || '🏴';
};

// NCAA Tournament Bracket Types
export type NCAARegion = 'East' | 'West' | 'South' | 'Midwest';

export interface BracketTeam {
  id: string;
  name: string;
  seed: number;
  region: NCAARegion;
  conference?: string;
  eliminated?: boolean;
  roundEliminated?: string;
}

export interface BracketMatchup {
  id: string;
  round: string;
  region: NCAARegion | 'Final Four';
  team1?: BracketTeam;
  team2?: BracketTeam;
  winner?: BracketTeam;
  gameTime?: string;
  gameDate?: string;
  location?: string;
  score1?: number;
  score2?: number;
}

export interface TournamentBracket {
  tournament: {
    name: string;
    year: number;
    startDate: string;
    endDate: string;
    status: 'TEMPLATE' | 'ACTIVE' | 'IN_PROGRESS' | 'COMPLETED';
  };
  regions: NCAARegion[];
  teams: BracketTeam[];
  matchups: BracketMatchup[];
  schedule: {
    firstFour: { dates: string[]; description: string };
    roundOf64: { dates: string[]; description: string };
    roundOf32: { dates: string[]; description: string };
    sweet16: { dates: string[]; description: string };
    elite8: { dates: string[]; description: string };
    finalFour: { date: string; description: string };
    championship: { date: string; description: string };
  };
}

// Standard bracket matchup order (seed pairings)
export const BRACKET_MATCHUP_ORDER = [
  [1, 16], [8, 9], [5, 12], [4, 13], [6, 11], [3, 14], [7, 10], [2, 15]
];

// Round names in order
export const TOURNAMENT_ROUNDS = [
  'First Four',
  'Round of 64',
  'Round of 32',
  'Sweet 16',
  'Elite 8',
  'Final Four',
  'Championship'
];
